/**
 * emoji.js — Central emoji vocabulary (issue #139).
 *
 * Single source of truth for the small set of pictographs the v2 UI
 * uses for at-a-glance recognition. Pull from here instead of inlining
 * a literal so the visual language stays consistent across catalogue
 * cards, widget chrome, the SpyBar, toasts and dashboards.
 *
 * Adding a new emoji? Put it here first, then reference `EMOJI.foo`.
 * If two surfaces would naturally use the same icon, they MUST share
 * the key — drift is the bug this file exists to prevent.
 *
 * Keep this list small. The aim is a vocabulary, not a sticker pack.
 */

export const EMOJI = Object.freeze({
  // ── Domain entities ──────────────────────────────────────────────
  sales:    '💰',  // gold/sales/money
  sale:     '🛒',  // a single sale event
  bigsale:  '🔥',  // notable / heat marker
  guild:    '🏰',  // guild / trader
  kiosk:    '🏪',  // guild kiosk / shop
  item:     '📦',  // a single item
  user:     '👤',  // player / discord user
  raffle:   '🎟️',  // raffle ticket
  feed:     '📡',  // live feed / streaming
  errors:   '⛔',  // recent errors panel
  perf:     '⚡',  // server performance
  ai:       '🐱',  // Fissal — the cat avatar IS the AI brand
  clock:    '🕒',  // generic time
  esoTime:  '🌙',  // ESO time of day
  alert:    '🚨',  // alerts
  warn:     '⚠',
  info:     'ℹ',
  ok:       '✓',
  err:      '✖',
  star:     '⭐',
  bot:      '🤖',  // Discord bot / automation
  search:   '🔍',
  sync:     '🔄',
  weekly:   '📅',
  history:  '📋',
  leader:   '👑',  // leaderboard
  trophy:   '🏆',  // leaderboard milestone / raffle podium

  // ── Trader sub-types ────────────────────────────────────────────
  post:      '⚔',
  caravan:   '🐎',
  dealers:   '💼',
  bank:      '🏦',  // banker NPC in city
  wayshrine: '🕯',  // wayshrine near kiosk

  // ── Chrome / UI ──────────────────────────────────────────────────
  window:   '🪟',  // generic dashboard / window default
  dashboards:'📑',
  add:      '＋',
  widgets:  '🔧',  // widgets tab in the home menu (#116)
  menu:     '☰',
  close:    '✖',
  theme:    '🎨',
  settings: '⚙',
  account:  '👤',
  layout:   '▦',
  spy:      '🛰',  // admin spying / impersonation
  spying:   '🕵',
  admin:    '🛰',  // admin marker (reuse of spy)
});

/**
 * Convenience helpers — keep these terse so callers stay readable.
 */
export function entityEmoji(kind) {
  switch (kind) {
    case 'item':  return EMOJI.item;
    case 'user':  return EMOJI.user;
    case 'guild': return EMOJI.guild;
    default:      return EMOJI.window;
  }
}

export default EMOJI;
